// src/features/home/components/VideoUpdate.js
import React, { useMemo, useRef, useEffect, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import {
  BsChevronLeft,
  BsChevronRight,
  BsYoutube,
  BsLightningCharge,
} from "react-icons/bs";
import { FaPodcast } from "react-icons/fa";

import "bootstrap/dist/css/bootstrap.min.css";
import "../../../shared/styles/CardSlideForm.css";
import "../../../shared/styles/VideoUpdate.css";
import "../../../shared/styles/UtillitiesComponent.css";

import { db } from "../../../shared/libs/firebaseConfig";
import { collection, getDocs, limit, orderBy, query, where } from "firebase/firestore";

import { bumpVideoView } from "../../../shared/utils/bumpVideoView";

function toDateSafe(v) {
  try {
    if (v?.toDate) return v.toDate();
    if (v instanceof Date) return v;
    if (typeof v === "number") return new Date(v);
    if (typeof v === "string") return new Date(v);
  } catch {}
  return null;
}

function videoHasCategory(video, category) {
  if (!category || category === "all") return true;
  const cats = Array.isArray(video?.categories) ? video.categories : [];
  return cats.includes(category);
}

function getYouTubeId(url = "") {
  try {
    const u = new URL(url);

    // https://www.youtube.com/watch?v=VIDEO_ID
    if (u.searchParams.get("v")) return u.searchParams.get("v");

    // https://youtu.be/VIDEO_ID
    if (u.hostname.includes("youtu.be")) {
      return u.pathname.replace("/", "").split("?")[0] || null;
    }

    // https://www.youtube.com/embed/VIDEO_ID
    if (u.pathname.startsWith("/embed/")) {
      return u.pathname.split("/embed/")[1]?.split("?")[0] || null;
    }

    // https://www.youtube.com/shorts/VIDEO_ID
    if (u.pathname.includes("/shorts/")) {
      return u.pathname.split("/shorts/")[1]?.split("?")[0] || null;
    }

    return null;
  } catch {
    return null;
  }
}

function ytEmbedUrl(videoId) {
  return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`;
}

export default function VideoUpdate({
  kind = "youtube",
  kinds = null,
  title,
  moreTo = "/videos",
  moreLabel = "ดูเพิ่มเติม",
  maxFetch = 40,
  selectedCategory = "all",
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [videosBase, setVideosBase] = useState([]);

  const [playingId, setPlayingId] = useState(null);

  const queryKinds = Array.isArray(kinds) && kinds.length > 0 ? kinds : [kind];
  const primaryKind = queryKinds[0] || kind;

  const computedTitle =
  title ??
  (kind === "youtube"
    ? "YouTube"
    : kind === "appetizer"
    ? "ปูพื้นฐานเรียกน้ำย่อย"
    : kind === "oral_exam_tips"
    ? "เทคนิคสอบปากเปล่า"
    : kind === "podcast"
    ? "Podcast"
    : "Shorts");

  useEffect(() => {
    setPlayingId(null);
  }, [selectedCategory, kind]);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        setError("");

        const q = query(
          collection(db, "videos"),
          where("isActive", "==", true),
          where("kind", "in", queryKinds.slice(0, 10)),
          orderBy("updatedAt", "desc"),
          limit(maxFetch)
        );

        const snap = await getDocs(q);
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        if (!alive) return;
        setVideosBase(rows);
      } catch (e) {
        console.error(e);
        if (!alive) return;
        setError(e?.message || "โหลดวิดีโอไม่สำเร็จ");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [kind, maxFetch, queryKinds.join("|")]);

  const videos = useMemo(() => {
    const arr = videosBase
      .filter((v) => videoHasCategory(v, selectedCategory))
      .slice();

    arr.sort((a, b) => {
      const da = toDateSafe(a.updatedAt) || toDateSafe(a.createdAt) || new Date(0);
      const dbb = toDateSafe(b.updatedAt) || toDateSafe(b.createdAt) || new Date(0);
      return dbb.getTime() - da.getTime();
    });

    return arr;
  }, [videosBase, selectedCategory]);

  const scrollerRef = useRef(null);
  const initialScrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    el.scrollLeft = 0;
    initialScrollRef.current = null;

    const updateArrows = () => {
      const now = scrollerRef.current;
      if (!now) return;

      const { scrollLeft, scrollWidth, clientWidth } = now;

      if (initialScrollRef.current === null) {
        initialScrollRef.current = scrollLeft;
      }
      const base = initialScrollRef.current ?? 0;

      if (scrollWidth <= clientWidth + 1) {
        setCanScrollLeft(false);
        setCanScrollRight(false);
        return;
      }

      const atStart = scrollLeft <= base + 1;
      const atEnd = scrollLeft + clientWidth >= scrollWidth - 1;

      setCanScrollLeft(!atStart);
      setCanScrollRight(!atEnd);
    };

    updateArrows();
    el.addEventListener("scroll", updateArrows, { passive: true });
    window.addEventListener("resize", updateArrows);

    return () => {
      el.removeEventListener("scroll", updateArrows);
      window.removeEventListener("resize", updateArrows);
    };
  }, [videos.length, selectedCategory, kind]);

  const handleNext = () => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: el.clientWidth * 0.85, behavior: "smooth" });
  };

  const handlePrev = () => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: -el.clientWidth * 0.85, behavior: "smooth" });
  };

  const Icon =
  primaryKind === "youtube" ||
  primaryKind === "appetizer" ||
  primaryKind === "oral_exam_tips"
    ? BsYoutube
    : primaryKind === "podcast"
    ? FaPodcast
    : BsLightningCharge;

    

  return (
    <>
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h3 className="mb-0">
          <strong>
            <Icon style={{ marginRight: 6 }} /> {computedTitle}
          </strong>
        </h3>

        <a href={moreTo} style={{ textDecoration: "none" }}>
          <Button variant="link" className="p-0 text-decoration-none">
            {moreLabel} <span aria-hidden="true">›</span>
          </Button>
        </a>
      </div>

      {loading && (
        <div className="text-center" style={{ marginTop: 40, marginBottom: 90 }}>
          <Spinner animation="border" role="status" />
        </div>
      )}

      {error && <div className="text-danger my-3">{error}</div>}

      {!loading && !error && videos.length === 0 && (
        <div className="text-muted">ยังไม่มีรายการ</div>
      )}

      {!loading && !error && videos.length > 0 && (
        <div className="slide-wrapper">
          {canScrollLeft && (
            <button
              type="button"
              className="slide-arrow slide-arrow-left"
              onClick={handlePrev}
              aria-label="เลื่อนซ้าย"
            >
              <BsChevronLeft size={34} />
            </button>
          )}

          {canScrollRight && (
            <button
              type="button"
              className="slide-arrow slide-arrow-right"
              onClick={handleNext}
              aria-label="เลื่อนขวา"
            >
              <BsChevronRight size={34} />
            </button>
          )}

          <div ref={scrollerRef} className="article-slide-container">
            {videos.map((v) => {
              const itemKind = v.kind || kind;
              const d = toDateSafe(v.updatedAt) || toDateSafe(v.createdAt);
              const dateText = d
                ? d.toLocaleDateString("th-TH", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })
                : "";

              const href = v.url || v.fileUrl || "#";

              const isYouTubeLike =
                itemKind === "youtube" ||
                itemKind === "appetizer" ||
                itemKind === "oral_exam_tips" ||
                itemKind === "podcast" ||
                itemKind === "shorts" ||
                itemKind === "shorts_prepare_for_exams";

              const isShorts =
                itemKind === "shorts" || itemKind === "shorts_prepare_for_exams";
              const videoId = isYouTubeLike ? getYouTubeId(href) : null;
              const isPlaying = playingId === v.id;

              return (
                <div
                  key={v.id}
                  className={`slide-item ${isShorts ? "slide-item--shorts" : ""}`}
                >
                  <div className="card app-card h-100">
                    <div
                      className={`position-relative bg-surface ${
                        isShorts ? "video-frame-shorts" : "ratio ratio-16x9"
                      }`}
                    >
                      {isYouTubeLike && videoId && isPlaying ? (
                        <iframe
                          src={ytEmbedUrl(videoId)}
                          title={v.title}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                          style={{ border: 0, width: "100%", height: "100%" }}
                        />
                      ) : (
                        <>
                          <img
                            src={
                              v.thumbUrl ||
                              (videoId
                                ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`
                                : "")
                            }
                            alt={v.title}
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />

                          {isYouTubeLike && videoId ? (
                            <button
                              type="button"
                              className={`video-play-btn ${
                                itemKind === "podcast"
                                  ? "video-play-btn--podcast"
                                  : itemKind === "shorts" || itemKind === "shorts_prepare_for_exams"
                                  ? "video-play-btn--shorts"
                                  : "video-play-btn--youtube"
                              }`}
                              onClick={async () => {
                                setPlayingId(v.id);
                                try {
                                  await bumpVideoView(v.id);
                                } catch {}
                              }}
                              aria-label="เล่นวิดีโอ"
                            >
                              {itemKind === "podcast" ? (
                                <FaPodcast size={46} className="video-play-icon" />
                              ) : itemKind === "shorts" || itemKind === "shorts_prepare_for_exams" ? (
                                <BsLightningCharge size={46} className="video-play-icon" />
                              ) : (
                                <BsYoutube size={46} className="video-play-icon" />
                              )}
                            </button>
                          ) : (
                            <a
                              href={href}
                              target="_blank"
                              rel="noreferrer"
                              className="stretched-link"
                              aria-label="เปิดลิงก์"
                            />
                          )}
                        </>
                      )}
                    </div>

                    <div className="card-body">
                      <div className="fw-semibold" style={{ lineHeight: 1.25 }}>
                        {v.title}
                      </div>

                      <div className="text-secondary small mt-1">
                        {dateText}
                        {Array.isArray(v.categories) && v.categories.length > 0 ? (
                          <> • {v.categories[0]}</>
                        ) : null}
                      </div>

                      {isYouTubeLike && videoId && (
                        <div className="mt-2">
                          <a
                            href={href}
                            target="_blank"
                            rel="noreferrer"
                            className="small text-decoration-none"
                          >
                            เปิดใน{" "}
                            {itemKind === "podcast"
                              ? "Podcast"
                              : itemKind === "shorts" || itemKind === "shorts_prepare_for_exams"
                              ? "Shorts"
                              : "YouTube"}{" "}
                            ›
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}